import { AUTH_TOKEN_KEY, getApiBaseUrl, isPublicAuthRequestUrl } from '../utils/authUtils';
import {
  clearAuthOnRefreshFailure,
  getStoredAccessToken,
  persistAccessToken,
  refreshAuthSession,
  shouldAttemptAuthRefresh,
} from './authSession';

const BACKEND_URL = getApiBaseUrl();

/**
 * Fetch JSON from an API endpoint with authentication.
 *
 * - Sends Bearer token when available
 * - Always includes credentials for session cookie auth
 * - On 401: attempts /auth/refresh once (single-flight), then retries once
 *
 * IMPORTANT: This function reads the response body exactly once.
 *
 * @param {string} url - API endpoint (relative or absolute)
 * @param {object} options - Fetch options including signal for AbortController
 * @returns {Promise<any>} - Parsed JSON response
 * @throws {Error} - Error with status, message, and data properties
 */
export async function apiFetchJson(url, options = {}) {
  const fullUrl = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  const skipAuth = isPublicAuthRequestUrl(fullUrl);
  const { signal, headers, credentials, _authRetried, ...restOptions } = options;

  const token = getStoredAccessToken();
  const requestHeaders = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(headers || {}),
  };

  if (!skipAuth && token && !requestHeaders.Authorization) {
    requestHeaders.Authorization = `Bearer ${token}`;
  }
  if (skipAuth && requestHeaders.Authorization) {
    delete requestHeaders.Authorization;
  }

  let response;
  try {
    response = await fetch(fullUrl, {
      ...restOptions,
      signal,
      credentials: credentials || 'include',
      headers: requestHeaders,
    });
  } catch (fetchError) {
    if (fetchError.name === 'AbortError') {
      throw fetchError;
    }
    const error = new Error('Network error. Please check your connection.');
    error.status = 0;
    error.data = null;
    throw error;
  }

  if (
    response.status === 401
    && shouldAttemptAuthRefresh(fullUrl, { retried: Boolean(_authRetried) })
  ) {
    // Cookie-only: do not mint JWT via /auth/refresh when no stored access token.
    if (!getStoredAccessToken()) {
      // fall through to error handling below
    } else {
      const refreshedToken = await refreshAuthSession();
      if (refreshedToken) {
        persistAccessToken(refreshedToken);
        return apiFetchJson(url, {
          ...options,
          headers: {
            ...(headers || {}),
            Authorization: `Bearer ${refreshedToken}`,
          },
          _authRetried: true,
        });
      }
      clearAuthOnRefreshFailure();
    }
  }

  const clonedResponse = response.clone();

  let rawText = '';
  try {
    rawText = await response.text();
  } catch (bodyError) {
    if (bodyError.name === 'AbortError') {
      throw bodyError;
    }
    try {
      rawText = await clonedResponse.text();
    } catch {
      if (!response.ok) {
        const error = new Error(`Request failed with status ${response.status}`);
        error.status = response.status;
        error.data = null;
        throw error;
      }
      return null;
    }
  }

  let data = null;
  try {
    data = rawText ? JSON.parse(rawText) : null;
  } catch {
    data = { message: rawText };
  }

  if (!response.ok) {
    const message =
      data?.detail
      || data?.message
      || data?.error
      || `Request failed with status ${response.status}`;

    const error = new Error(typeof message === 'string' ? message : JSON.stringify(message));
    error.status = response.status;
    error.data = data;
    if (data && typeof data === 'object') {
      if (data.code != null) error.code = data.code;
      if (data.field != null) error.field = data.field;
    }
    throw error;
  }

  return data;
}

export { AUTH_TOKEN_KEY };
export default apiFetchJson;
